import { Box, SxProps, Typography } from '@mui/material';
import { FC, PropsWithChildren, ReactElement, createContext, useCallback, useContext, useMemo, useState } from 'react';

export type Message = string | ReactElement;

export interface InfoPopupContextProps {
	infoPopup: {
		isOpen: boolean;
		title?: Message;
		message?: Message;
		sx?: SxProps;
	};
	showInfoPopup(data: { title?: Message; message?: Message; sx?: SxProps }): void;
}

const defaultData: InfoPopupContextProps = {
	infoPopup: {
		isOpen: false,
		title: '',
		message: '',
		sx: {},
	},
	showInfoPopup: () => undefined,
};

const InfoPopupContext = createContext<InfoPopupContextProps>(defaultData);

export const InfoPopupProvider: FC<PropsWithChildren<any>> = ({ children }) => {
	const [infoPopup, setInfoPopup] = useState<InfoPopupContextProps['infoPopup']>(defaultData.infoPopup);

	const showInfoPopup = useCallback<InfoPopupContextProps['showInfoPopup']>(({ title, message, sx = {} }) => {
		setInfoPopup({
			isOpen: Boolean(title || message),
			title,
			message,
			sx,
		});

		setTimeout(() => {
			setInfoPopup(defaultData.infoPopup);
		}, 3000);
	}, []);

	const contextValue = useMemo(
		() => ({
			infoPopup,
			showInfoPopup,
		}),
		[infoPopup, showInfoPopup]
	);

	return (
		<InfoPopupContext.Provider value={contextValue}>
			{children}
			<Box
				sx={{
					display: infoPopup.isOpen ? 'flex' : 'none',
					flexFlow: 'column nowrap',
					alignItems: 'flex-start',
					rowGap: '0.5rem',
					position: 'fixed',
					zIndex: 10000,
					bottom: 30,
					left: '50%',
					transform: 'translateX(-50%)',
					width: 'fit-content',
					minWidth: '180px',
					maxWidth: '40vw',
					padding: '0.8rem 1.2rem',
					border: '1px solid',
					borderColor: 'rgb(0, 0, 0, 0.3)',
					borderRadius: '100px',
					backgroundColor: '#fff',
					...(infoPopup.sx ?? {}),
				}}
			>
				{infoPopup.title && (
					<Box>
						{typeof infoPopup.title === 'string' ? (
							<Typography
								variant="h6"
								sx={{
									fontWeight: '700',
									fontSize: '20px',
								}}
							>
								{infoPopup.title}
							</Typography>
						) : (
							infoPopup.title
						)}
					</Box>
				)}
				{infoPopup.message && (
					<Box>
						{typeof infoPopup.message === 'string' ? (
							<Typography variant="body1">{infoPopup.message}</Typography>
						) : (
							infoPopup.message
						)}
					</Box>
				)}
			</Box>
		</InfoPopupContext.Provider>
	);
};

export const useInfoPopup = () => {
	const context = useContext(InfoPopupContext);

	if (!context) {
		throw new Error('useInfoPopup must be used within a InfoPopupProvider');
	}

	return context;
};

export default InfoPopupContext;
